/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {EUserPermissionAccess, IUser} from "./types";
import {EArgosRouteKeys, ROUTES_TO_PERMISSIONS} from "../../../Routes/constants";
import useGetCurrentPath from "../../../hooks/useGetCurrentPath";
import {useAppSelector} from "../../hooks";
import {selectCurrentUser} from "./slice";
import ADAS_PROGRAMS_CONSTANTS from "../../../containers/pages/AdasPrograms/constants";
import {VEHICLE_PROGRAMS_CONSTANTS} from "../../../containers/pages/vehiclePrograms/constants";
import { SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS } from "../../../containers/pages/SensingSystemArchitectureDesign/constants";

export const getIsUserAdmin = (user: IUser) => user?.permissions?.[EArgosRouteKeys.ADMIN] ?? false
export const useGetIsUserAdmin = () => {
  const user = useAppSelector(selectCurrentUser)
  return getIsUserAdmin(user)
}
const getUserPermissions = (user: any) => user?.permissions
export const getRouteAccessLevel = (route: string, user: any): EUserPermissionAccess => {
  return user?.permissions?.userArgosRoutePermissions?.[ROUTES_TO_PERMISSIONS?.[route]] ?? EUserPermissionAccess.NO_ACCESS
}
export const getRouteKeyAccessLevel = (routeKey: EArgosRouteKeys, user: IUser): EUserPermissionAccess => {
  return user?.permissions?.userArgosRoutePermissions?.[routeKey] ?? EUserPermissionAccess.NO_ACCESS
}

const exemptRoutes = new Set<string>(
  [
    '/landing',
    '/',
    ADAS_PROGRAMS_CONSTANTS.itemDetails.coverageRequirements.viewReportRoute,
    VEHICLE_PROGRAMS_CONSTANTS.itemDetails.sensingSystemPerformance.viewReportRoute,
  ]
)

// these are routes that even if a user has admin: true, if the userArgosRoutePermissions is set to "No Access", it will not appear
const restrictedRoutes = new Set<string>([
  '/potentiallymovingobjects',
  '/objectclassification',
  '/signs',
  '/miscellaneousObjects',
  SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.landing.route,
  SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.route,
]);

const adminRoutes = new Set<string>([
  '/admin',
  '/html/index.html'
]);

export const hasAccessToRoute = (route: string, user: IUser): boolean => {
    const accessLevel = getRouteAccessLevel(route, user);
    const isAdmin = getIsUserAdmin(user);

    // Check if the route is in restrictedRoutes
    if (restrictedRoutes.has(route)) {
        // Allow access if the user has READ_ONLY or READ_WRITE permissions
        return (
            accessLevel === EUserPermissionAccess.READ_ONLY || 
            accessLevel === EUserPermissionAccess.READ_WRITE
        );
    }
    // Check if the route is in adminRoutes
    if (adminRoutes.has(route)) {
        // Allow access if the user is an admin OR if they have READ_ONLY or READ_WRITE permissions
        return (
            isAdmin || 
            (accessLevel === EUserPermissionAccess.READ_ONLY || 
             accessLevel === EUserPermissionAccess.READ_WRITE)
        );
    }

    // For non-restricted routes, check general access conditions
    return accessLevel !== EUserPermissionAccess.NO_ACCESS || exemptRoutes.has(route);
};

export const useHasAccessToRoute = (route: string): boolean => {
  const user = useAppSelector(selectCurrentUser)
  return hasAccessToRoute(route, user)
}
export const useHasAccessToCurrentRoute = (): boolean => {
  const currentPath = useGetCurrentPath()
  const user = useAppSelector(selectCurrentUser)
  if (currentPath == null) return false
  return hasAccessToRoute(currentPath, user)
}
export const useGetCurrentPageUserAccessLevel = (): [boolean, EUserPermissionAccess | ''] => {
  const currentPath: string | undefined = useGetCurrentPath()
  const user = useAppSelector(selectCurrentUser)
  // TODO: NOTE: Because we have path: '*' in ARGOS_ROUTES, useGetCurrentPath() won't return undefined even if current route is not in ARGOS_ROUTES explicitly; instead, it will give the route. Should we change?
  if (currentPath == null) return [false, ""]
  const isUserAdmin = getIsUserAdmin(user);
  const accessLevel = getRouteAccessLevel(currentPath, user) ?? ""
  return [isUserAdmin, accessLevel]
}

export const useHasWriteAccess = (ignoreAdmin: boolean = false): boolean => {
  const [isUserAdmin, accessLevel] = useGetCurrentPageUserAccessLevel()
  if (ignoreAdmin) {
    return accessLevel === EUserPermissionAccess.READ_WRITE
  } else {
    return isUserAdmin || accessLevel === EUserPermissionAccess.READ_WRITE
  }
}

export const useCurrentUserHasThisAccessLevelToRouteKey = (routeKey: EArgosRouteKeys, accessLevel: EUserPermissionAccess): boolean => {
  const user = useAppSelector(selectCurrentUser)
  const userAccessLevel = getRouteKeyAccessLevel(routeKey, user)
  return getIsUserAdmin(user) || userAccessLevel === accessLevel
}

export const hasThisAccessLevelToRouteKey = (routeKey: EArgosRouteKeys, user: IUser, accessLevel: EUserPermissionAccess): boolean => {
  const userAccessLevel = getRouteKeyAccessLevel(routeKey, user)
  return getIsUserAdmin(user) || userAccessLevel === accessLevel
}


/**
 * Retrieves the access level for a given route key from the user's permissions.
 * @param {EArgosRouteKeys} key - The route key for which to check permissions.
 * @param {IUser} user - The user whose permissions are being checked.
 * @returns {EUserPermissionAccess} - The access level of the user for the given route key.
 */
export const getPermissionGivenKey = (key: EArgosRouteKeys, user: IUser) => user?.permissions?.userArgosRoutePermissions?.[key] ?? EUserPermissionAccess.NO_ACCESS
 
/**
 * Hook to get the access level for a given route key from the current user's permissions.
 * @param {EArgosRouteKeys} key - The route key for which to check permissions.
 * @returns {EUserPermissionAccess} - The access level of the current user for the given route key.
 */
export const useGetPermissionGivenKey = (key: EArgosRouteKeys) => {
  const user = useAppSelector(selectCurrentUser)
  return getPermissionGivenKey(key, user)
}
 
/**
 * Hook to check if the current user has write access to a given route key.
 * @param {EArgosRouteKeys} key - The route key to check for write access.
 * @returns {boolean} - True if the user has write access, false otherwise.
 */
export const useHasWriteAccessGivenKey = (key: EArgosRouteKeys) => {
  const permission = useGetPermissionGivenKey(key)
  return permission === EUserPermissionAccess.READ_WRITE
}